/**
 * The heat-sabre art (id heat-sabre; display name from the game data), hand-made in the sample-page style (replaces
 * the C3 generator skeleton). The art's own look: a heavy sabre at forge heat (re-themed from blood to ember 10-13) —
 * thick red-hot cleaver bands with a white fresh edge and molten drips off their lower side, sparks flung on a falling
 * arc that die as cinders where they land, scorch marks with a glowing rim left on the ground, steel scale splintering. No gold, no
 * butterflies, no silk (that is brocade). Each move is staged anticipation -> strike -> impact -> dissipation; body-moving
 * moves leave split-body afterimages. Original designs.
 */
import { add, diamond, hash, isoRing, lerp, line, px, type G, type V } from '../kit';
import type { Scene } from '../skills';
import { C, bladeArc, clamp01, fade, hitStar, level, ringOn, since, strikeAt, tilesPx, type ArtFx } from '../families/swordKit';

// ── The art's tones ──

const WH = C('white', 8);
// Heated steel, hottest to coolest (10-13: blood -> ember): yellow-hot, orange, rust, cooling cinnabar iron, soot.
const H2 = C('brown', 2);
const H3 = C('fire', 3);
const H4 = C('fire', 2);
const H5 = C('fire', 3);
const H6 = C('fire', 4);
const H7 = C('fire', 5);
const F3 = C('fire', 3);
const F4 = C('fire', 4);
const F5 = C('fire', 5);
const F6 = C('fire', 6);
const SCALE = C('steel', 5);
const SCALE_D = C('steel', 3);

/** Mirror a screen angle for a move facing left. */
const sideA = (a: number, side: number): number => (side > 0 ? a : Math.PI - a);
const sideOf = (s: Scene): number => (s.d[0] >= 0 ? 1 : -1);

// ── The art's pieces ──

/**
 * A heavy cleaver band: a crescent round `c` of radius `r` from `a0` to `a1` (the leading end), `w` rows deep (1 -> w
 * -> 1 along it, the lead the thick end), the outer row white while `fresh`, maroon rows inside, squashed by `sy`. Molten
 * metal hangs off the band in short drips (`drip` cells long at most), grown by `e`.
 */
function cleaver(g: G, c: V, r: number, a0: number, a1: number, w: number, lvl: number, fresh: boolean, sy = 1, drip = 0, seed = 0): void {
  if (lvl <= 0) return;
  level(g, lvl);
  const n = Math.max(10, Math.round(Math.abs(a1 - a0) * r * 1.7));
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const prof = Math.min(1, u * 1.5) * Math.min(1, (1 - u) * 6);
    const rows = Math.max(1, Math.round(1 + (w - 1) * prof));
    const a = a0 + (a1 - a0) * u;
    const ca = Math.cos(a);
    const sa = Math.sin(a) * sy;
    for (let k = rows - 1; k >= 0; k--) {
      const col = k === 0 ? (fresh ? WH : H7) : k === rows - 1 && rows > 2 ? H3 : H5;
      px(g, c[0] + ca * (r - k), c[1] + sa * (r - k), col);
    }
    // Drips off the band's lower rows.
    if (drip > 0 && i % 3 === 0 && hash(i, seed) > 0.55 && u > 0.15) {
      const L = Math.round(drip * (0.4 + 0.6 * hash(i, seed + 1)));
      const k = rows - 1;
      const x = c[0] + ca * (r - k);
      const y = c[1] + sa * (r - k);
      for (let j = 1; j <= L; j++) px(g, x, y + j, j === L ? H5 : H3);
    }
  }
  g.globalAlpha = 1;
}

/** One falling spark at (x, y): a hot head and a cooling tail (`big`: 2 wide). */
function drop(g: G, x: number, y: number, big: boolean): void {
  // Few tones per row: the flush writes each row's runs, so a drop is 3 runs (4 when big).
  px(g, x, y - 1, H7);
  g.fillStyle = H5;
  g.fillRect(Math.round(x) - (big ? 1 : 0), Math.round(y), big ? 3 : 2, 1);
  g.fillStyle = H3;
  g.fillRect(Math.round(x) - (big ? 1 : 0), Math.round(y) + 1, big ? 3 : 2, 1);
  if (big) px(g, x, y + 2, H3);
}

/**
 * Sparks flung from `c` along `dir` (a fan of `spread` rad), `n` drops, `e` frames after; they fall and die flat on
 * the ground line `gy` (a short cinder smear) instead of going below it.
 */
function spray(g: G, c: V, dir: V, e: number, n: number, seed: number, spread: number, speed: number, gy: number, life = 12): void {
  if (e < 0 || e >= life) return;
  const a0 = Math.atan2(dir[1], dir[0]);
  level(g, fade(e, life * 0.55, life * 0.8));
  for (let i = 0; i < n; i++) {
    const a = a0 + (hash(i, seed) - 0.5) * spread;
    const v = speed * (0.55 + 0.7 * hash(i, seed + 1));
    const x = c[0] + Math.cos(a) * v * e;
    let y = c[1] + Math.sin(a) * v * e * 0.7 - 1.2 * e + 0.32 * e * e;
    if (y >= gy) {
      y = gy;
      px(g, x - 2, y, H3);
      px(g, x - 1, y, H4);
      px(g, x, y, H4);
      px(g, x + 1, y, H3);
      continue;
    }
    drop(g, x, y, i % 2 === 0);
  }
  g.globalAlpha = 1;
}

/**
 * Scorch shapes, cached: one flat list of row runs (dx, dy, length, tone 0..2) per (whole radius, seed), built on first use
 * and kept (a handful of shapes per art), so a scorch costs a few runs per frame, not a per-cell lobe test.
 */
const SCORCH_TONES = [H2, C('fire', 1), H5] as const;
const SCORCHES = new Map<number, Int16Array>();
function scorchShape(r: number, seed: number): Int16Array {
  const key = r * 4096 + seed;
  const hit = SCORCHES.get(key);
  if (hit) return hit;
  const out: number[] = [];
  const ry = r * 0.5;
  const p1 = hash(1, seed) * 6;
  const p2 = hash(2, seed) * 6;
  for (let y = Math.floor(-ry * 1.4); y <= Math.ceil(ry * 1.4); y++) {
    let runX = 0;
    let runT = -1;
    let runL = 0;
    const flush = (): void => {
      if (runT >= 0) out.push(runX, y, runL, runT);
      runT = -1;
      runL = 0;
    };
    for (let x = Math.floor(-r * 1.4); x <= Math.ceil(r * 1.4); x++) {
      const a = Math.atan2(y / ry, x / r);
      const lim = 1 + 0.22 * Math.sin(3 * a + p1) + 0.12 * Math.sin(5 * a + p2);
      const d = Math.hypot(x / r, y / ry);
      const t = d > lim ? -1 : d > lim - 0.22 ? (x > 0 && y < 0 ? 2 : 1) : 0;
      if (t === runT && t >= 0) {
        runL++;
        continue;
      }
      flush();
      if (t >= 0) {
        runX = x;
        runT = t;
        runL = 1;
      }
    }
    flush();
  }
  // Satellite drops.
  for (let i = 0; i < 4; i++) {
    const a = hash(i, seed + 5) * Math.PI * 2;
    const k = 1.35 + hash(i, seed + 6) * 0.4;
    out.push(Math.round(Math.cos(a) * r * k), Math.round(Math.sin(a) * ry * k), 1, 1);
  }
  const arr = Int16Array.from(out);
  SCORCHES.set(key, arr);
  return arr;
}

/** A scorch mark on the ground at `c`, `r` cells across (iso, half as tall), lobed, a glowing rim on the upper right. */
function scorch(g: G, c: V, r: number, seed: number, lvl: number): void {
  const rr = Math.round(r);
  if (lvl <= 0 || rr < 2) return;
  level(g, lvl);
  const sh = scorchShape(rr, seed);
  const cx = Math.round(c[0]);
  const cy = Math.round(c[1]);
  for (let i = 0; i < sh.length; i += 4) {
    g.fillStyle = SCORCH_TONES[sh[i + 3]!]!;
    g.fillRect(cx + sh[i]!, cy + sh[i + 1]!, sh[i + 2]!, 1);
  }
  g.globalAlpha = 1;
}

/** Embers rising off `c` (fire, drifting up), `e` frames on. */
function embers(g: G, c: V, e: number, n: number, seed: number, spreadX: number, life = 12): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life * 0.5, life * 0.8));
  for (let i = 0; i < n; i++) {
    const k = (e + hash(i, seed) * life) % life;
    const x = c[0] + (hash(i, seed + 1) - 0.5) * spreadX + Math.sin(k * 0.7 + i) * 1.5;
    const y = c[1] - k * (1.6 + hash(i, seed + 2));
    px(g, x, y, k < life * 0.4 ? F6 : F4);
    px(g, x, y + 1, F3);
  }
  g.globalAlpha = 1;
}

/** The foe scorched: hot sparks running off its body and falling to its feet (the burn status), `e` frames on. */
function burning(g: G, s: Scene, e: number, life: number): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life - 6, life - 3));
  for (let i = 0; i < 4; i++) {
    const x = s.f[0] + (i - 1.5) * 3 + (hash(i, 9) - 0.5) * 2;
    const y0 = s.f[1] - s.chest * (0.8 + hash(i, 10) * 0.5);
    const k = (e + i * 3) % 9;
    line(g, [x, y0], [x, y0 + Math.min(k, 3)], H6);
    if (k > 3) drop(g, x, y0 + k * 2, false);
  }
  g.globalAlpha = 1;
}

/** A flame tongue standing on `b`, `h` cells tall, swaying with `ph`: maroon base, fire body, a light tip. */
function flame(g: G, b: V, h: number, ph: number): void {
  for (let y = 0; y < h; y++) {
    const u = y / h;
    const w = Math.max(0, Math.round((1 - u) * 3 - 0.2));
    const x0 = b[0] + Math.sin(ph + u * 3) * u * 2.5;
    const col = u < 0.25 ? H4 : u < 0.55 ? F4 : u < 0.8 ? F5 : F6;
    g.fillStyle = col;
    g.fillRect(Math.round(x0) - w, Math.round(b[1] - y), w * 2 + 1, 1);
  }
}

/** A bead of heat flowing (`head` white-lit). */
function bead(g: G, p: V, head: boolean): void {
  diamond(g, p[0], p[1], head ? 2 : 1, H5, head ? H7 : H6);
  if (head) px(g, p[0] - 1, p[1] - 1, WH);
}

/** A quadratic path from `a` to `b` bowed up by `lift`. */
const arcPath = (a: V, b: V, lift: number, u: number): V => {
  const m: V = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2 - lift];
  return lerp(lerp(a, m, u), lerp(m, b, u), u);
};

/** Steel scale flung off `c` (the break): pale slivers, dark ends, falling. */
function splinters(g: G, c: V, e: number, n: number, seed: number, life = 10): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life * 0.55, life * 0.8));
  for (let i = 0; i < n; i++) {
    const a = -Math.PI * (0.1 + 0.8 * hash(i, seed));
    const v = 2.4 + hash(i, seed + 1) * 2;
    const x = c[0] + Math.cos(a) * v * e;
    const y = c[1] + Math.sin(a) * v * e * 0.8 + 0.3 * e * e;
    const t = (i + e) % 2;
    px(g, x, y, WH);
    px(g, x + (t ? 1 : 0), y + (t ? 0 : 1), SCALE);
    px(g, x + (t ? 2 : 0), y + (t ? 0 : 2), SCALE);
    px(g, x + (t ? 3 : 0), y + (t ? 0 : 3), SCALE_D);
    px(g, x + (t ? 1 : 1), y + 1, SCALE_D);
  }
  g.globalAlpha = 1;
}

/** A cracked ground under `c`: dark cracks radiating, fire glowing in them early. */
function cracks(g: G, c: V, e: number, r: number, seed: number, life = 16): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life * 0.55, life * 0.8));
  const grow = clamp01((e + 1) / 3);
  for (let i = 0; i < 7; i++) {
    let a = (i / 7) * Math.PI * 2 + hash(i, seed) * 0.6;
    let p: V = c;
    const L = r * (0.6 + 0.4 * hash(i, seed + 1)) * grow;
    for (let k = 0; k < 4; k++) {
      a += (hash(i * 4 + k, seed + 2) - 0.5) * 0.7;
      const q: V = [p[0] + (Math.cos(a) * L) / 4, p[1] + (Math.sin(a) * L * 0.5) / 4];
      line(g, p, q, e < 4 && k < 2 ? F4 : H2);
      line(g, [p[0], p[1] + 1], [q[0], q[1] + 1], e < 4 && k < 2 ? F3 : H3);
      p = q;
    }
  }
  g.globalAlpha = 1;
}

/** Dazed: three sparks orbiting over the foe's head. */
function dazed(g: G, s: Scene, e: number, life: number): void {
  if (e < 0 || e >= life) return;
  const head: V = [s.f[0], s.f[1] - s.chest * 1.9];
  level(g, fade(e, life - 6, life - 3));
  for (let i = 0; i < 3; i++) {
    const a = e * 0.6 + (i * Math.PI * 2) / 3;
    drop(g, head[0] + Math.cos(a) * 7, head[1] + Math.sin(a) * 2.5, Math.sin(a) > 0);
  }
  g.globalAlpha = 1;
}

/** A glint at the sabre's tip over a wind-up, fire-lit. */
function tipGlint(g: G, s: Scene, t0: number, t1: number): void {
  if (s.t < t0 || s.t >= t1) return;
  const { tip } = s.limbs(s.t);
  level(g, 3);
  diamond(g, tip[0], tip[1], s.t < (t0 + t1) / 2 ? 1 : 2, F4, WH);
  g.globalAlpha = 1;
}

// ── 1. bs-slash: the sabre raised and lit, one heavy chop down through the foe, sparks sprayed out behind, a scorch ──

const SL_HIT = 0.6;
const slash: ArtFx = {
  id: 'blood-slash',
  skill: 'bs-slash',
  th: 'bs-slash',
  on: true,
  note: 'ยกดาบใหญ่ขึ้น ประกายไฟที่ปลายดาบ (ตั้งท่า) -> ฟันลงหนักผ่าเป้า แถบดาบร้อนแดงหนา เหล็กหลอมหยดจากขอบ -> สะเก็ดไฟสาดออกหลังเป้า ตกพื้นเป็นรอยไหม้ -> สะเก็ดไฟไหลจากเป้า จาง',
  weapon: 'sword',
  acts: [{ m: 'wxCleave', t0: 0.2, t1: 1.0 }],
  hits: [SL_HIT],
  big: SL_HIT,
  pre: 0.4,
  hit: SL_HIT,
  end: 1.6,
  afterimage: null,
  under(g, s) {
    const e = since(s.t, SL_HIT + 0.1);
    if (e >= 0 && e < 12) scorch(g, add(s.f, s.d, 8), Math.min(13, 5 + e * 1.5), 11, fade(e, 8, 10));
  },
  over(g, s) {
    tipGlint(g, s, 0.25, 0.45);
    bladeArc(g, s, 0.4, 0.75, 'fire', 4);
    const e = since(s.t, SL_HIT);
    if (e < 0) return;
    const c = strikeAt(s);
    const sd = sideOf(s);
    // The chop: a tall cleaver band from over the foe's head down past its hip.
    if (e < 6) cleaver(g, [c[0] - sd * 16, c[1] - 2], 34, sideA(-1.45, sd), sideA(0.8, sd), e < 2 ? 9 : 6, fade(e, 3, 5), e < 2, 1, 3 + e * 1.5, 3);
    spray(g, add(c, s.d, 4), s.d, e, 18, 21, 1.4, 3.2, s.f[1] + 2);
    hitStar(g, s, c, e, 'fire', 1.3);
    burning(g, s, e - 3, 12);
  },
};

// ── 2. bs-rend: two cuts crossing in an X, each throwing sparks; the X brand left glowing ──

const RE_HITS = [0.5, 0.88] as const;
const rend: ArtFx = {
  id: 'blood-rend',
  skill: 'bs-rend',
  th: 'bs-rend',
  on: true,
  note: 'ก้าวเข้า ฟันเฉียงลงครั้งแรก สะเก็ดไฟสาด -> พลิกดาบฟันเฉียงสวนกลับเป็นรูปกากบาท สะเก็ดไฟสาดหนักกว่า -> รอยตรากากบาทร้อนค้างที่เป้า สะเก็ดไฟหยด สองชั้น -> จาง',
  weapon: 'sword',
  acts: [
    { m: 'wxCleave', t0: 0.2, t1: 0.6 },
    { m: 'wxCleave', t0: 0.6, t1: 1.05 },
  ],
  hits: [...RE_HITS],
  big: RE_HITS[1],
  ghosts: [0.45, 0.83],
  stepIn: 7,
  pre: 0.3,
  hit: RE_HITS[0],
  end: 2.0,
  afterimage: 'port',
  under(g, s) {
    const e = since(s.t, RE_HITS[1] + 0.1);
    if (e >= 0 && e < 13) {
      scorch(g, add(s.f, s.d, 4), Math.min(7, 3 + e), 31, fade(e, 9, 11));
      scorch(g, add(s.f, s.d, -6), Math.min(5, 2 + e), 32, fade(e, 9, 11));
    }
  },
  over(g, s) {
    bladeArc(g, s, 0.3, 0.6, 'fire', 3);
    bladeArc(g, s, 0.7, 0.98, 'fire', 4);
    const c = strikeAt(s);
    // The two cuts: down-right, then up-left across it.
    const cut = (at: number, from: V, to: V, w: number, seed: number): void => {
      const e = since(s.t, at);
      if (e < 0 || e >= 5) return;
      const L = e < 1 ? 0.7 : 1;
      level(g, fade(e, 3, 4));
      const a = add(c, from, L);
      const b = add(c, to, L);
      for (let k = 0; k < w; k++) line(g, [a[0] + k * 0.5, a[1] + k], [b[0] + k * 0.5, b[1] + k], k === 0 ? (e < 2 ? WH : H7) : k === w - 1 ? H3 : H5);
      g.globalAlpha = 1;
      spray(g, c, [s.d[0] * 0.8, -0.6], e, 6 + w * 3, seed, 1.6, 2.8, s.f[1] + 2, 10);
    };
    cut(RE_HITS[0], [-22, -24], [22, 22], 5, 41);
    cut(RE_HITS[1], [-22, 20], [24, -24], 6, 42);
    // The X brand left on the foe, glowing.
    const e = since(s.t, RE_HITS[1] + 0.2);
    if (e >= 0 && e < 14) {
      level(g, fade(e, 8, 11));
      for (const o of [0, 1]) {
        line(g, [c[0] - 8 + o, c[1] - 8], [c[0] + 8 + o, c[1] + 8], o ? H3 : H5);
        line(g, [c[0] - 8 + o, c[1] + 8], [c[0] + 8 + o, c[1] - 8], o ? H3 : H5);
      }
      px(g, c[0], c[1], H6);
      g.globalAlpha = 1;
    }
    hitStar(g, s, c, since(s.t, RE_HITS[0]), 'fire', 0.8);
    hitStar(g, s, c, since(s.t, RE_HITS[1]), 'fire', 1.2);
    burning(g, s, e, 16);
  },
};

// ── 3. bs-whirl: a ring drawn round the feet, one full spinning sweep at knee height, sparks thrown out all round ──

const WH_HIT = 0.7;
const WH_HIT2 = 1.15;
const WH_R = Math.min(60, tilesPx(2.4));
/** The sweep: a flat cleaver band chasing round the caster; the back half under, the front half over. */
const whirlBand = (g: G, s: Scene, front: boolean): void => {
  for (const [h, k] of [[WH_HIT, 0], [WH_HIT2, 1]] as const) {
    const e = since(s.t, h - 0.15);
    if (e < 0 || e > 6) continue;
    const head = -Math.PI / 2 + (e / 4) * Math.PI * 2 * (k ? -1 : 1);
    const span = 2.6;
    const rx = WH_R * (k ? 0.95 : 0.8);
    const steps = Math.ceil(span * rx);
    level(g, fade(e, 4, 6));
    for (let i = 0; i <= steps; i++) {
      const u = i / steps;
      const a = head - (k ? -1 : 1) * span * (1 - u);
      const sn = Math.sin(a);
      if (sn >= 0 !== front) continue;
      const rows = Math.max(1, Math.round(1 + 6 * u));
      for (let q = rows - 1; q >= 0; q--) {
        const r = rx - q;
        px(g, s.a[0] + Math.cos(a) * r, s.a[1] + sn * r * 0.5 - 10 - q, q === 0 ? (u > 0.7 ? WH : H7) : q === rows - 1 && rows > 2 ? H3 : H5);
      }
    }
    g.globalAlpha = 1;
  }
};
const whirl: ArtFx = {
  id: 'blood-whirl',
  skill: 'bs-whirl',
  th: 'bs-whirl',
  on: true,
  note: 'วงไฟวาดรอบเท้าบอกรัศมี -> หมุนตัวกวาดดาบรอบตัวระดับเข่า แถบดาบชาดวนเต็มวง สะเก็ดไฟถูกเหวี่ยงออกทุกทิศ -> กวาดย้อนอีกรอบ -> รอยไหม้เป็นวงรอบตัว จาง',
  weapon: 'sword',
  acts: [
    { m: 'wxCleave', t0: 0.3, t1: 0.8 },
    { m: 'wxRise', t0: 0.8, t1: 0.9 },
    { m: 'wxCleave', t0: 0.9, t1: 1.4 },
  ],
  hits: [WH_HIT, WH_HIT2],
  big: WH_HIT,
  ghosts: [0.6, 0.68, 0.76],
  stepIn: 5,
  pre: 0.45,
  hit: WH_HIT,
  end: 2.0,
  afterimage: 'port',
  under(g, s) {
    if (s.t >= 0.2 && s.t < WH_HIT) {
      level(g, 2);
      ringOn(g, s.a, WH_R, -Math.PI / 2, (s.t - 0.2) / 0.4, F4, true);
      g.globalAlpha = 1;
    }
    // Scorches round the ring.
    const e = since(s.t, WH_HIT + 0.15);
    if (e >= 0 && e < 14)
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.4;
        scorch(g, [s.a[0] + Math.cos(a) * WH_R * 0.95, s.a[1] + Math.sin(a) * WH_R * 0.48], Math.min(7, 3 + e), 50 + i, fade(e, 9, 12));
      }
    whirlBand(g, s, false);
  },
  over(g, s) {
    whirlBand(g, s, true);
    // Sparks thrown out on every side, both sweeps.
    for (const [h, seed] of [[WH_HIT, 60], [WH_HIT2, 70]] as const) {
      const e = since(s.t, h);
      if (e < 0 || e >= 10) continue;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + (seed - 60) * 0.05;
        const from: V = [s.a[0] + Math.cos(a) * WH_R * 0.6, s.a[1] + Math.sin(a) * WH_R * 0.3 - 8];
        spray(g, from, [Math.cos(a), Math.sin(a) * 0.5], e, 3, seed + i, 0.8, 2.8, from[1] + 11, 9);
      }
    }
    hitStar(g, s, strikeAt(s), since(s.t, WH_HIT), 'fire', 0.9);
  },
};

// ── 4. bs-charge: embers spurt from the feet, a red-hot dash leaving scorched steps, a flat cleaving cut on arrival ──

const CH_LEN = 56;
const CH_GO = 0.5;
const CH_HIT = 0.7;
const charge: ArtFx = {
  id: 'blood-charge',
  skill: 'bs-charge',
  th: 'bs-charge',
  on: true,
  note: 'ย่อตัว ดาบลากต่ำ ถ่านไฟพุ่งจากเท้า (ตั้งท่า) -> พุ่งเข้าหาเป้า เส้นเหล็กร้อนแดงลากตามตัว รอยเท้าไหม้บนพื้น เงาร่างแยก -> ฟันแนวราบกว้างเต็มแรงเมื่อถึงตัว สะเก็ดไฟสาดไปข้างหน้า -> จาง',
  weapon: 'sword',
  acts: [{ m: 'wxThrust', t0: 0.3, t1: 1.2 }],
  hits: [CH_HIT],
  big: CH_HIT,
  ghosts: [0.55, 0.6, 0.65],
  cast: [0.05, 0.5],
  pre: 0.5,
  hit: CH_HIT,
  end: 2.2,
  dash: CH_LEN,
  afterimage: 'dash',
  move(s, t) {
    const k0 = clamp01((t - CH_GO) / 0.18);
    const k = 1 - (1 - k0) * (1 - k0);
    const back = 1 - clamp01((t - 1.9) / 0.3);
    return [s.d[0] * CH_LEN * k * back, s.d[1] * CH_LEN * k * back];
  },
  under(g, s) {
    // Scorched steps along the path (fire, cooling to maroon).
    const e = since(s.t, CH_GO);
    if (e < 0 || e > 16) return;
    level(g, fade(e, 9, 13));
    for (let i = 0; i < 4; i++) {
      const u = (i + 0.5) / 4;
      if (u > (e + 1) / 3) continue;
      const p = add(s.a, s.d, CH_LEN * u * 0.9);
      const o = i % 2 ? 2 : -2;
      const col = e < 6 ? F4 : H3;
      line(g, [p[0] - 2, p[1] + o], [p[0] + 2, p[1] + o], col);
      px(g, p[0], p[1] + o - 1, e < 6 ? F6 : H4);
    }
    g.globalAlpha = 1;
  },
  over(g, s) {
    // Wind-up: embers off the feet, the sabre's glint.
    if (s.t >= 0.1 && s.t < CH_GO + 0.1) embers(g, s.a, since(s.t, 0.1), 7, 80, 14, 8);
    tipGlint(g, s, 0.2, CH_GO);
    // The dash: hot streaks dragged behind the body.
    const go = since(s.t, CH_GO);
    if (go >= 0 && go < 5) {
      const b = s.body(s.t);
      level(g, fade(go, 3, 4));
      for (let i = 0; i < 5; i++) {
        const y = b[1] - s.chest * (0.3 + i * 0.32);
        const L = 10 + hash(i, 81) * 14 - go * 2;
        const x0 = b[0] - s.d[0] * 6;
        line(g, [x0, y], [x0 - s.d[0] * L, y - s.d[1] * L], i % 2 ? H5 : H6);
        px(g, x0, y, WH);
      }
      g.globalAlpha = 1;
    }
    // The flat cut: a wide, low-bowed cleaver across the foe.
    const e = since(s.t, CH_HIT);
    if (e < 0) return;
    const c = strikeAt(s, 1.0);
    const sd = sideOf(s);
    if (e < 6) cleaver(g, [c[0] - sd * 6, c[1] - 12], 42, sideA(-0.2, sd), sideA(2.6, sd), e < 2 ? 9 : 6, fade(e, 3, 5), e < 2, 0.45, 4, 5);
    spray(g, add(c, s.d, 10), s.d, e, 18, 85, 0.9, 3.8, s.f[1] + 3, 12);
    hitStar(g, s, c, e, 'fire', 1.3);
    burning(g, s, e - 3, 12);
  },
};

// ── 5. bs-break: a fire ring tell, the sabre raised overhead, a slam down, the guard and ground cracked, steel scale flies ──

const BR_HIT = 0.75;
const brk: ArtFx = {
  id: 'blood-break',
  skill: 'bs-break',
  th: 'bs-break',
  on: true,
  note: 'วงไฟส้มวาดรอบเท้า ดาบยกสูงเหนือหัว ถ่านไฟหยดจากดาบ (ตั้งท่าทำลาย) -> ทุ่มดาบลงตรงๆ แถบดาบหนาที่สุด -> พื้นใต้เป้าแตกร้าวมีไฟในรอย สะเก็ดเหล็กกระเด็น -> ประกายไฟวนเหนือหัว (มึน) จาง',
  weapon: 'sword',
  acts: [{ m: 'wxBreak', t0: 0.15, t1: 1.1 }],
  hits: [BR_HIT],
  big: BR_HIT,
  ghosts: [0.6, 0.66, 0.72],
  stepIn: 8,
  cast: [0.05, 0.5],
  pre: 0.5,
  hit: BR_HIT,
  end: 2.2,
  afterimage: 'port',
  under(g, s) {
    if (s.t >= 0.05 && s.t < BR_HIT) {
      level(g, 3);
      ringOn(g, s.a, 15, -Math.PI / 2, (s.t - 0.05) / 0.45, F4, true);
      g.globalAlpha = 1;
    }
    cracks(g, s.f, since(s.t, BR_HIT), 40, 90);
  },
  over(g, s) {
    // Wind-up: embers dripping off the raised sabre.
    if (s.t >= 0.2 && s.t < BR_HIT) {
      const { tip } = s.limbs(s.t);
      const w = since(s.t, 0.2);
      level(g, 3);
      diamond(g, tip[0], tip[1], w % 2 ? 2 : 1, F4, WH);
      for (let i = 0; i < 3; i++) {
        const k = (w + i * 2) % 6;
        px(g, tip[0] + (i - 1) * 2, tip[1] + 3 + k * 2, k < 3 ? F5 : F3);
      }
      g.globalAlpha = 1;
    }
    bladeArc(g, s, 0.6, 0.9, 'fire', 5);
    const e = since(s.t, BR_HIT);
    if (e < 0) return;
    const c = strikeAt(s);
    const sd = sideOf(s);
    // The slam: the thickest band, straight down through the foe.
    if (e < 5) cleaver(g, [c[0] - sd * 12, c[1] + 6], 36, sideA(-1.75, sd), sideA(0.35, sd), e < 2 ? 10 : 7, fade(e, 3, 4), e < 2, 1, 4, 7);
    // Shock: a fire flash on the ground under the foe.
    if (e < 4) {
      level(g, fade(e, 2, 3));
      isoRing(g, s.f[0], s.f[1], 12 + e * 7, e < 2 ? F5 : H4, true);
      if (e < 2) isoRing(g, s.f[0], s.f[1], 8 + e * 4, F6, true);
      g.globalAlpha = 1;
    }
    splinters(g, c, e - 1, 14, 95);
    spray(g, c, [s.d[0], -0.8], e, 10, 97, 1.8, 2.4, s.f[1] + 2);
    hitStar(g, s, c, e, 'fire', 1.7);
    dazed(g, s, e - 4, 16);
  },
};

// ── 6. bs-frenzy: a heartbeat on the ground, a red flame aura, the sabre held across with a hot guard waiting ──

const FR_ON = 0.5;
const frenzy: ArtFx = {
  id: 'blood-frenzy',
  skill: 'bs-frenzy',
  th: 'bs-frenzy',
  on: true,
  note: 'ชีพจรเต้นสองจังหวะเป็นวงบนพื้น -> เปลวไฟแดงลุกรอบตัว -> ดาบตั้งขวาง โค้งเหล็กร้อนตั้งรับเต้นตามชีพจรอยู่ด้านหน้า (รอรับท่า) ถ่านไฟลอย -> จาง',
  weapon: 'sword',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 2.0 }],
  hits: [],
  pre: 0.1,
  hit: FR_ON,
  end: 2.6,
  afterimage: null,
  under(g, s) {
    const e = since(s.t, 0.1);
    if (e < 0 || e > 28) return;
    // Heartbeat: a double pulse every 10 frames.
    for (const off of [0, 2]) {
      const k = (e - off) % 10;
      if (e - off < 0 || k > 5) continue;
      level(g, k < 2 ? 3 : k < 4 ? 2 : 1);
      isoRing(g, s.a[0], s.a[1], 12 + k * 5, off ? H4 : H6, true);
    }
    g.globalAlpha = 1;
    scorch(g, s.a, Math.min(10, 3 + Math.floor(e / 2)), 101, fade(e, 20, 25));
    // The aura's back half.
    const lv = fade(e, 20, 24);
    if (e >= 3) {
      level(g, lv);
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 + 0.3;
        if (Math.sin(a) >= 0) continue;
        flame(g, [s.a[0] + Math.cos(a) * 13, s.a[1] + Math.sin(a) * 6], 16 + ((e + i * 3) % 5) * 3, e * 0.8 + i);
      }
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    const e = since(s.t, 0.1);
    if (e < 0 || e > 28) return;
    if (e >= 3) {
      level(g, fade(e, 20, 24));
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 + 0.3;
        if (Math.sin(a) < 0) continue;
        flame(g, [s.a[0] + Math.cos(a) * 13, s.a[1] + Math.sin(a) * 6], 12 + ((e + i * 3) % 4) * 3, e * 0.8 + i);
      }
      g.globalAlpha = 1;
    }
    embers(g, [s.a[0], s.a[1] - s.chest * 0.6], e, 7, 110, 28, 29);
    // The waiting guard: an upright red-hot crescent in front, beating with the heart.
    const v = since(s.t, FR_ON);
    if (v >= 0 && v < 22) {
      const sd = sideOf(s);
      const beat = v % 10 < 2;
      const o = add([s.a[0], s.a[1] - s.chest * 0.9], s.d, -4);
      const lv = v < 2 ? 2 : v < 17 ? 3 : 1;
      cleaver(g, o, beat ? 30 : 28, sideA(-1.05, sd), sideA(1.05, sd), beat ? 6 : 4, lv, beat, 1, beat ? 4 : 0, 113);
    }
  },
};

// ── 7. bs-drink: a cut, then the foe's heat drawn out in a stream of beads back into the caster ──

const DR_HIT = 0.6;
const DR_FLOW = 0.75;
const drink: ArtFx = {
  id: 'blood-drink',
  skill: 'bs-drink',
  th: 'bs-drink',
  on: true,
  note: 'ประกายปลายดาบ -> ฟันเฉียงขึ้น สะเก็ดไฟพุ่ง -> ความร้อนจากเป้าถูกดูดเป็นสายลูกปัดไฟโค้งกลับเข้าดาบและอกผู้ใช้ -> อกเรืองแดง ถ่านไฟลอยขึ้น (ฟื้นพลัง) -> จาง',
  weapon: 'sword',
  acts: [{ m: 'wxCleave', t0: 0.2, t1: 1.0 }],
  hits: [DR_HIT],
  big: DR_HIT,
  pre: 0.4,
  hit: DR_HIT,
  end: 2.2,
  afterimage: null,
  over(g, s) {
    tipGlint(g, s, 0.25, 0.45);
    bladeArc(g, s, 0.4, 0.72, 'fire', 3);
    const c = strikeAt(s);
    const sd = sideOf(s);
    const e = since(s.t, DR_HIT);
    if (e < 0) return;
    if (e < 5) cleaver(g, [c[0] - sd * 14, c[1] + 8], 30, sideA(1.3, sd), sideA(-0.7, sd), e < 2 ? 8 : 5, fade(e, 3, 4), e < 2, 1, 3, 121);
    spray(g, c, [0, -1], e, 12, 123, 1.0, 2.2, s.f[1] + 2, 9);
    hitStar(g, s, c, e, 'fire', 1);
    // The stream: beads drawn from the foe's wound back along a high arc into the caster's chest.
    const chest: V = [s.a[0], s.a[1] - s.chest];
    const f = since(s.t, DR_FLOW);
    if (f >= 0 && f < 14) {
      level(g, fade(f, 10, 12));
      for (let i = 0; i < 12; i++) {
        const u = (f - i * 0.5) / 7;
        if (u < 0 || u > 1) continue;
        const p = arcPath(c, chest, s.chest * 1.1, u);
        bead(g, p, i === 0 || u > 0.85);
        if (i % 2 === 0) px(g, p[0] + 2, p[1] + 2, H4);
      }
      g.globalAlpha = 1;
    }
    // The caster fed: the chest glows, red sparks rising round the body.
    const h = since(s.t, DR_FLOW + 0.55);
    if (h >= 0 && h < 14) {
      level(g, h < 3 ? 3 : 2);
      diamond(g, chest[0], chest[1], h < 3 ? 5 : 3, H5, H7);
      if (h < 3) diamond(g, chest[0], chest[1], 2, H7, WH);
      level(g, fade(h, 8, 11));
      for (let i = 0; i < 6; i++) {
        const x = s.a[0] + (hash(i, 127) - 0.5) * 24;
        const y = s.a[1] - ((h * 2 + i * 7) % (s.chest * 1.8));
        diamond(g, x, y, 1, H5, i % 2 ? H7 : WH);
      }
      g.globalAlpha = 1;
    }
    burning(g, s, e - 2, 12);
  },
};

// ── 8. bs-massacre (ท่าใหญ่): rage gathered as forge heat, then a red-hot tornado of sabres spinning round the caster ──

const MA_SPIN = 1.0;
/** The game's spin: 6 s, a blow every 0.4 s (data/skills.ts bs-massacre spin); the last blow is the finale. */
const MA_HITS: readonly number[] = Array.from({ length: 15 }, (_, i) => Math.round((1.1 + i * 0.4) * 100) / 100);
const MA_FIN = MA_HITS[MA_HITS.length - 1]!;
const MA_END = MA_SPIN + 6;
const MA_R = Math.min(70, tilesPx(2.8));
/** The tornado: three cleaver blades orbiting at two heights, plus drops flung off them (back half or front half). */
const tornado = (g: G, s: Scene, front: boolean): void => {
  // The blades spin until the finale, where they are flung out as the wide cleavers (drawn in `over`).
  if (s.t < MA_SPIN || s.t >= MA_FIN) return;
  const e = since(s.t, MA_SPIN);
  const lv = 3;
  const grow = clamp01((e + 1) / 3);
  for (let i = 0; i < 3; i++) {
    const rot = e * 0.95 + (i / 3) * Math.PI * 2;
    const rx = MA_R * (0.6 + 0.3 * (i % 2)) * grow;
    const hgt = 6 + (i % 2) * s.chest * 0.8;
    const span = 1.3;
    level(g, lv);
    const n = Math.ceil(span * rx);
    for (let k = 0; k <= n; k++) {
      const u = k / n;
      const a = rot - span * (1 - u);
      const sn = Math.sin(a);
      if (sn >= 0 !== front) continue;
      const rows = Math.max(1, Math.round(1 + 4 * u));
      for (let q = rows - 1; q >= 0; q--) {
        const r = rx - q;
        px(g, s.a[0] + Math.cos(a) * r, s.a[1] + sn * r * 0.5 - hgt - q, q === 0 ? (u > 0.75 ? WH : H7) : q === rows - 1 && rows > 2 ? H3 : H5);
      }
      // Sparks thrown off the blade's tail.
      if (k % 14 === 0 && u < 0.6) drop(g, s.a[0] + Math.cos(a) * (rx + 4 + (e % 3) * 2), s.a[1] + sn * (rx + 4) * 0.5 - hgt + 2 + (e % 3), false);
    }
  }
  g.globalAlpha = 1;
};
const massacre: ArtFx = {
  id: 'blood-massacre',
  skill: 'bs-massacre',
  th: 'bs-massacre',
  on: true,
  note: 'รอยไหม้แผ่ใต้เท้า ไอร้อนลอยขึ้นรวมที่ดาบ วงไฟวาดบอกรัศมี (สะสมโทสะ) -> หมุนดาบ พายุดาบชาดสามใบวนสองระดับรอบตัว สะเก็ดไฟถูกเหวี่ยงออก ฟันซ้ำเป็นจังหวะ -> ลูกปัดไฟไหลกลับเข้าตัว (ดูดพลัง) -> พายุจาง รอยไหม้ค้าง',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 0.95 },
    ...MA_HITS.map((h) => ({ m: 'wxCleave' as const, t0: h - 0.1, t1: h + 0.3 })),
    { m: 'wxRise', t0: MA_END, t1: MA_END + 0.2 },
  ],
  hits: [...MA_HITS],
  big: MA_FIN,
  ghosts: [1.0, 1.08, 1.16, MA_FIN - 0.08, MA_FIN],
  stepIn: 0,
  cast: [0.1, 0.95],
  pre: 0.85,
  hit: MA_HITS[0]!,
  end: MA_END + 0.8,
  afterimage: 'port',
  beats: [
    { t: 0.1, th: 'ไอร้อนรวม' },
    { t: MA_SPIN, th: 'พายุดาบชาด' },
    { t: MA_HITS[2]!, th: 'ดูดพลัง' },
    { t: MA_FIN, th: 'ฟันปิดท้าย' },
    { t: MA_END, th: 'พายุจาง' },
  ],
  under(g, s) {
    const w = since(s.t, 0.1);
    const wEnd = Math.round((MA_END + 0.6) * 12);
    if (w >= 0 && w < wEnd) scorch(g, s.a, Math.min(14, 4 + w * 0.8), 131, fade(w, wEnd - 6, wEnd - 3));
    if (s.t >= 0.35 && s.t < MA_SPIN) {
      level(g, 2);
      ringOn(g, s.a, MA_R, -Math.PI / 2, (s.t - 0.35) / 0.55, F4, true);
      g.globalAlpha = 1;
    }
    // The spin's ground: the ring lit with fire, pulsing with every blow.
    const e = since(s.t, MA_SPIN);
    const out = since(s.t, MA_END);
    if (e >= 0 && out < 6) {
      const k = since(s.t, MA_HITS[0]!) % 5;
      const hitNow = s.t >= MA_HITS[0]! && s.t < MA_FIN + 0.2 && k < 2;
      level(g, out < 0 ? 2 : fade(out, 2, 4));
      isoRing(g, s.a[0], s.a[1], MA_R, hitNow ? F5 : H5);
      if (s.t < MA_FIN) isoRing(g, s.a[0], s.a[1], Math.round(MA_R * 0.7), H3);
      g.globalAlpha = 1;
    }
    const fe = since(s.t, MA_FIN);
    if (fe >= 0 && fe < 8) {
      level(g, fade(fe, 3, 6));
      isoRing(g, s.a[0], s.a[1], Math.round(MA_R * (0.8 + fe * 0.1)), fe < 2 ? F6 : F4, fe < 2);
      isoRing(g, s.a[0], s.a[1], Math.round(MA_R * (0.6 + fe * 0.1)), H5);
      g.globalAlpha = 1;
    }
    tornado(g, s, false);
  },
  over(g, s) {
    // Gathering: heat shimmer rising from the ground up to the raised sabre.
    if (s.t >= 0.1 && s.t < MA_SPIN) {
      const { tip } = s.limbs(s.t);
      const w = since(s.t, 0.1);
      level(g, 3);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const from: V = [s.a[0] + Math.cos(a) * 26, s.a[1] + Math.sin(a) * 12];
        const u = ((w + i * 1.5) % 9) / 9;
        const p = lerp(from, tip, u * u);
        bead(g, p, u > 0.7);
      }
      diamond(g, tip[0], tip[1], w % 2 ? 3 : 2, H5, WH);
      g.globalAlpha = 1;
      embers(g, s.a, w, 6, 140, 30, 11);
    }
    tornado(g, s, true);
    // Each blow: sparks flung outward on all sides, a star on the foe.
    for (let k = 0; k < MA_HITS.length - 1; k++) {
      const e = since(s.t, MA_HITS[k]!);
      if (e < 0 || e >= 9) continue;
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + k * 0.8;
        const from: V = [s.a[0] + Math.cos(a) * MA_R * 0.6, s.a[1] + Math.sin(a) * MA_R * 0.3 - 10];
        spray(g, from, [Math.cos(a), Math.sin(a) * 0.5], e, 3, 150 + k * 4 + i, 0.9, 3, from[1] + 11, 8);
      }
      if (k === 0 || k % 3 === 2) hitStar(g, s, strikeAt(s), e, 'fire', k === 0 ? 1.5 : 0.9);
    }
    // The drink: heat beads from the foe back to the caster after every third blow.
    for (let j = 2; j < MA_HITS.length; j += 3) {
      const f = since(s.t, MA_HITS[j]!);
      if (f < 0 || f >= 12) continue;
      const c = strikeAt(s);
      const chest: V = [s.a[0], s.a[1] - s.chest];
      level(g, fade(f, 8, 10));
      for (let i = 0; i < 6; i++) {
        const u = (f - i * 0.7) / 6;
        if (u < 0 || u > 1) continue;
        bead(g, arcPath(c, chest, s.chest * 0.8, u), i === 0);
      }
      g.globalAlpha = 1;
    }
    // The finale (the last blow): the three blades flung out as wide cleavers, a double ring, sparks on every side.
    const fe = since(s.t, MA_FIN);
    if (fe >= 0 && fe < 12) {
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2 + 0.5;
        const r = MA_R * (0.7 + fe * 0.09);
        cleaver(g, [s.a[0], s.a[1] - 14], r, a, a + 1.2, fe < 2 ? 7 : 4, fade(fe, 4, 7), fe < 2, 0.5, 0, 160 + i);
      }
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const from: V = [s.a[0] + Math.cos(a) * MA_R * 0.7, s.a[1] + Math.sin(a) * MA_R * 0.35 - 10];
        spray(g, from, [Math.cos(a), Math.sin(a) * 0.5], fe, 3, 170 + i, 0.9, 3.4, from[1] + 11, 10);
      }
      hitStar(g, s, strikeAt(s), fe, 'fire', 1.8);
    }
    burning(g, s, since(s.t, MA_HITS[0]!) - 2, Math.round((MA_END - 1) * 12));
  },
};

/** The heat-sabre moves, hand-made (each its own shape, motion and timing; one palette and motif family). */
export const BLOODSABRE_FX: readonly ArtFx[] = [slash, rend, whirl, charge, brk, frenzy, drink, massacre];
